/**
 * All-Time Financial Summary
 * One-page lifetime snapshot: turnover, profit, stock purchases, records,
 * year-over-year growth chart.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, RefreshCw, TrendingUp, Award, Trophy, Zap, ShoppingCart,
  Package, Users, Building2, Wallet, Percent, Calendar, Crown, Flame,
  BarChart3, Sparkles, TrendingDown, Clock, Flag, ArrowUpRight,
  ArrowDownRight, Timer, Route,
} from "lucide-react";
import { toast } from "sonner";
import MilestoneWidget from "@/components/MilestoneWidget";
import CountUp from "@/components/CountUp";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, Area, AreaChart,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtR = (v) => `R ${(Number(v) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtRShort = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1_000_000) return `R ${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `R ${(n / 1_000).toFixed(1)}K`;
  return `R ${n.toFixed(0)}`;
};
const fmtInt = (n) => (Number(n) || 0).toLocaleString("en-ZA");
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

// ---------- BIG STAT tile ----------
const StatTile = ({ icon: Icon, label, value, sublabel, color = "purple", testid }) => {
  const colors = {
    purple: "from-purple-600 to-fuchsia-600",
    emerald: "from-emerald-600 to-teal-600",
    amber: "from-amber-500 to-orange-600",
    blue: "from-blue-600 to-cyan-600",
    rose: "from-rose-600 to-pink-600",
    slate: "from-slate-700 to-slate-900",
  };
  return (
    <div
      className={`relative rounded-2xl p-4 md:p-5 text-white shadow-lg overflow-hidden bg-gradient-to-br ${colors[color]}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider opacity-80 font-semibold">{label}</div>
          <div className="text-2xl md:text-3xl font-black mt-1 truncate">{value}</div>
          {sublabel && <div className="text-xs opacity-80 mt-1 truncate">{sublabel}</div>}
        </div>
        <div className="opacity-30 -mr-2 -mt-2">
          <Icon className="h-14 w-14" />
        </div>
      </div>
    </div>
  );
};

const RecordCard = ({ icon: Icon, title, value, sub, color = "amber" }) => {
  const border = { amber: "border-amber-300 bg-amber-50", emerald: "border-emerald-300 bg-emerald-50", blue: "border-blue-300 bg-blue-50", rose: "border-rose-300 bg-rose-50" }[color];
  const iconClr = { amber: "text-amber-600", emerald: "text-emerald-600", blue: "text-blue-600", rose: "text-rose-600" }[color];
  return (
    <div className={`border-2 rounded-xl p-4 ${border}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg bg-white ${iconClr}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-600">{title}</div>
          <div className="text-lg font-black text-gray-900 truncate">{value}</div>
          {sub && <div className="text-xs text-gray-600 mt-0.5">{sub}</div>}
        </div>
      </div>
    </div>
  );
};

// ------------- GROWTH MOMENTUM CARD ---------------
const GrowthMomentumCard = ({ growth }) => {
  const t = growth?.this_year_ytd || {};
  const l = growth?.last_year_same_period || {};
  const dt = growth?.delta_turnover_pct;
  const dx = growth?.delta_transactions_pct;
  const arrow = (v) => {
    if (v == null) return { Icon: TrendingUp, tone: "text-gray-400", label: "no prior data" };
    if (v >= 0) return { Icon: ArrowUpRight, tone: "text-emerald-600", label: `+${v}%` };
    return { Icon: ArrowDownRight, tone: "text-rose-600", label: `${v}%` };
  };
  const a1 = arrow(dt);
  const a2 = arrow(dx);
  return (
    <Card className="border-emerald-200" data-testid="growth-momentum">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-emerald-900">
          <TrendingUp className="h-4 w-4 text-emerald-600" /> Growth Momentum
        </CardTitle>
        <CardDescription>This year YTD vs same period last year</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center gap-3 border-l-4 border-emerald-500 pl-3">
          <a1.Icon className={`h-8 w-8 ${a1.tone}`} />
          <div>
            <div className="text-xs text-gray-600 uppercase font-semibold">Turnover</div>
            <div className={`text-2xl font-black ${a1.tone}`}>{a1.label}</div>
            <div className="text-xs text-gray-600">
              {fmtR(t.turnover)} vs {fmtR(l.turnover)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 border-l-4 border-blue-500 pl-3">
          <a2.Icon className={`h-8 w-8 ${a2.tone}`} />
          <div>
            <div className="text-xs text-gray-600 uppercase font-semibold">Transactions</div>
            <div className={`text-2xl font-black ${a2.tone}`}>{a2.label}</div>
            <div className="text-xs text-gray-600">
              {fmtInt(t.transactions)} vs {fmtInt(l.transactions)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ------------- FUN FACTS STRIP ---------------
const FunFactsStrip = ({ lifetime }) => {
  const cleanFacts = [
    { icon: "💰", text: `R100 notes stacked = ${((lifetime.turnover / 100) * 0.109 / 1000).toFixed(2)} km high` },
    { icon: "🚗", text: `Turnover ≈ ${Math.round(lifetime.turnover / 24).toLocaleString("en-ZA")} litres of petrol` },
    { icon: "🧱", text: `${fmtInt(lifetime.items_sold)} items ≈ ${Math.round(lifetime.items_sold / 500).toLocaleString("en-ZA")} bakkie-loads` },
    { icon: "🛒", text: `${fmtInt(lifetime.transaction_count)} baskets ≈ ${Math.round(lifetime.transaction_count / 20).toLocaleString("en-ZA")} full trolleys` },
    { icon: "🏪", text: `Customers served would fill ${Math.round(lifetime.transaction_count / 60).toLocaleString("en-ZA")} minibus taxis` },
    { icon: "⚡", text: `Avg basket R${(lifetime.average_transaction_value || 0).toFixed(0)} ≈ ${Math.round((lifetime.average_transaction_value || 0) / 20)}× a loaf of bread` },
  ];
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % cleanFacts.length), 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fact = cleanFacts[i];
  return (
    <Card className="border-fuchsia-200 bg-gradient-to-r from-fuchsia-50 via-pink-50 to-purple-50" data-testid="fun-facts">
      <CardContent className="py-3 flex items-center gap-4">
        <div className="text-4xl">{fact.icon}</div>
        <div className="flex-1">
          <div className="text-xs uppercase text-fuchsia-700 font-bold tracking-wider">Fun fact</div>
          <div className="text-base font-semibold text-gray-800">{fact.text}</div>
        </div>
        <div className="flex gap-1">
          {cleanFacts.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 w-4 rounded-full transition-all ${idx === i ? "bg-fuchsia-500" : "bg-fuchsia-200"}`}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ------------- DAY-OF-WEEK HEATMAP ---------------
const DayOfWeekHeatmap = ({ dow, peakHour }) => {
  const max = Math.max(...dow.map((d) => d.turnover), 1);
  const best = dow.reduce((a, b) => (b.turnover > a.turnover ? b : a), dow[0]);
  const worst = dow.reduce((a, b) => (b.turnover < a.turnover ? b : a), dow[0]);
  return (
    <Card data-testid="dow-heatmap">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="h-4 w-4 text-indigo-600" /> When Business Happens
        </CardTitle>
        <CardDescription>
          Your best day is <strong className="text-emerald-700">{best.day}</strong>, quietest is{" "}
          <strong className="text-rose-700">{worst.day}</strong>.
          {peakHour && (
            <> Peak hour: <strong>{peakHour.hour}:00 – {(peakHour.hour + 1) % 24}:00</strong> ({fmtR(peakHour.turnover)}).</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-2">
          {dow.map((d) => {
            const intensity = d.turnover / max;
            const alpha = Math.max(0.15, intensity);
            return (
              <div
                key={d.day}
                className="rounded-lg p-3 text-center transition-transform hover:scale-105"
                style={{
                  background: `rgba(79, 70, 229, ${alpha})`,
                  color: intensity > 0.5 ? "white" : "#1f2937",
                }}
                title={`${d.day}: ${fmtR(d.turnover)} across ${fmtInt(d.count)} txns`}
              >
                <div className="text-xs font-bold uppercase opacity-90">{d.day}</div>
                <div className="text-lg font-black mt-1">
                  {d.turnover >= 1000 ? `R${(d.turnover / 1000).toFixed(1)}k` : `R${d.turnover.toFixed(0)}`}
                </div>
                <div className="text-[10px] opacity-80">{fmtInt(d.count)} txns</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ------------- STORE TIMELINE ---------------
const iconMap = { flag: Flag, sparkles: Sparkles, flame: Flame, zap: Zap, crown: Crown };
const StoreTimeline = ({ events }) => (
  <Card data-testid="store-timeline">
    <CardHeader className="pb-2">
      <CardTitle className="text-base flex items-center gap-2">
        <Route className="h-4 w-4 text-purple-600" /> Store Timeline
      </CardTitle>
      <CardDescription>Key moments in your store's journey</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="overflow-x-auto pb-2">
        <div className="flex gap-3 min-w-max">
          {events.map((e, idx) => {
            const Icon = iconMap[e.icon] || Sparkles;
            return (
              <div key={idx} className="flex flex-col items-center min-w-[180px] relative">
                {idx < events.length - 1 && (
                  <div className="absolute top-6 left-1/2 w-full h-0.5 bg-purple-200" />
                )}
                <div className="z-10 h-12 w-12 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center text-white shadow-lg">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="text-xs font-bold text-gray-900 mt-2 text-center">{e.title}</div>
                <div className="text-[10px] text-gray-500">{fmtDate(e.date)}</div>
                <div className="text-[11px] text-gray-600 text-center mt-1 leading-tight">
                  {e.detail}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </CardContent>
  </Card>
);

// ------------- TOP-10 LIST ---------------
const TopList = ({ title, icon: Icon, color, rows, columns, emptyText, footer }) => {
  const headerBg = { purple: "bg-purple-50 text-purple-800", blue: "bg-blue-50 text-blue-800", amber: "bg-amber-50 text-amber-800" }[color];
  return (
    <Card>
      <CardHeader className={`pb-2 ${headerBg} rounded-t-xl`}>
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="h-4 w-4" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-3">
        {!rows.length ? (
          <p className="text-xs text-gray-500 py-4 text-center">{emptyText}</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="border-b text-[10px] uppercase text-gray-500">
              <tr>
                <th className="text-left pb-1 w-6">#</th>
                {columns.map((c) => (
                  <th key={c.key} className={`pb-1 ${c.align === "right" ? "text-right" : "text-left"}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 10).map((r, i) => (
                <tr key={r.id || r.product_id || r.name || i} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-1.5 text-gray-500 font-bold">{i + 1}</td>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`py-1.5 ${c.align === "right" ? "text-right" : ""} ${c.accent ? "font-bold text-gray-900" : "text-gray-700"} ${c.flex ? "truncate max-w-[180px]" : ""}`}
                      title={String(r[c.key] ?? "")}
                    >
                      {c.fmt ? c.fmt(r[c.key]) : r[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {footer && <div className="pt-3 mt-3 border-t">{footer}</div>}
      </CardContent>
    </Card>
  );
};



export default function AllTimeFinancialSummary() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/financial-summary/all-time`);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load all-time summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-purple-600" />
          <p className="mt-3 text-sm text-gray-600">Loading lifetime financials…</p>
        </div>
      </div>
    );
  }

  const lifetime = data?.lifetime || {};
  const records = data?.records || {};
  const yearlyData = data?.yearly_data || [];

  // Chart series
  const chartData = yearlyData.map((y) => ({
    year: String(y.year),
    turnover: y.turnover,
    net_profit: y.net_profit,
    gp_percent: y.gp_percent,
  }));

  const isEmpty = !yearlyData.length || lifetime.turnover === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-slate-100 pb-10" data-testid="all-time-page">
      {/* ---------- Header bar ---------- */}
      <div className="bg-slate-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/yearly-summary")}
            className="text-white hover:bg-white/10"
            data-testid="all-time-back"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Yearly Financials
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-black flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-amber-400" />
              All-Time Financials
            </h1>
            <p className="text-xs text-slate-300 mt-0.5">
              Everything since {fmtDate(data?.first_sale_date)} — {data?.years_in_business} years ({fmtInt(data?.days_in_business)} days)
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="border-white/20 bg-white/10 text-white hover:bg-white/20"
            data-testid="all-time-refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {isEmpty ? (
          <Card className="p-10 text-center text-gray-500">
            <BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-40" />
            No sales history yet. Ring up a few transactions and check back.
          </Card>
        ) : (
          <>
            {/* ---------- MONEY ODOMETER ---------- */}
            <Card className="border-2 border-purple-300 shadow-lg overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
              <CardContent className="py-8 text-center">
                <div className="text-xs uppercase tracking-widest opacity-70 mb-2 flex items-center justify-center gap-2">
                  <Timer className="h-4 w-4" /> Money Through The Till
                </div>
                <div className="text-6xl md:text-7xl font-black tracking-tight text-amber-300 tabular-nums drop-shadow-lg">
                  <CountUp to={lifetime.turnover} />
                </div>
                <div className="text-sm opacity-80 mt-2">
                  since {fmtDate(data?.first_sale_date)} — that's about{" "}
                  <span className="font-bold text-amber-200">
                    R {(lifetime.turnover / Math.max(1, data?.days_in_business * 24 * 3600)).toFixed(4)}
                  </span>{" "}
                  every second
                </div>
              </CardContent>
            </Card>

            {/* ---------- GROWTH MOMENTUM ---------- */}
            {data?.growth && (
              <GrowthMomentumCard growth={data.growth} />
            )}

            {/* ---------- FUN COMPARISONS ---------- */}
            <FunFactsStrip lifetime={lifetime} />

            {/* ---------- HERO: 3 giant stats ---------- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatTile
                icon={TrendingUp}
                color="purple"
                label="Lifetime Turnover"
                value={fmtRShort(lifetime.turnover)}
                sublabel={`${fmtR(lifetime.turnover)} incl. VAT`}
                testid="tile-lifetime-turnover"
              />
              <StatTile
                icon={Award}
                color="emerald"
                label="Lifetime Gross Profit"
                value={fmtRShort(lifetime.gross_profit)}
                sublabel={`${lifetime.gp_percent?.toFixed(1)}% margin`}
                testid="tile-lifetime-gp"
              />
              <StatTile
                icon={Wallet}
                color="amber"
                label="Lifetime Net Profit"
                value={fmtRShort(lifetime.net_profit)}
                sublabel={`After ${fmtRShort(lifetime.expenses)} expenses`}
                testid="tile-lifetime-net"
              />
            </div>

            {/* ---------- 6 secondary stats ---------- */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatTile icon={ShoppingCart} color="blue" label="Stock Purchases" value={fmtRShort(lifetime.stock_purchases)} sublabel={`${fmtInt(lifetime.grv_count)} GRVs`} testid="tile-stock" />
              <StatTile icon={Package} color="slate" label="Items Sold" value={fmtInt(lifetime.items_sold)} sublabel="Units through the till" testid="tile-items" />
              <StatTile icon={BarChart3} color="rose" label="Transactions" value={fmtInt(lifetime.transaction_count)} sublabel={`Avg ${fmtR(lifetime.average_transaction_value)}`} testid="tile-txns" />
              <StatTile icon={Users} color="purple" label="Customers" value={fmtInt(lifetime.customer_count)} sublabel="On file" testid="tile-cust" />
              <StatTile icon={Building2} color="emerald" label="Suppliers" value={fmtInt(lifetime.supplier_count)} sublabel="Registered" testid="tile-supp" />
              <StatTile icon={Package} color="amber" label="Active Products" value={fmtInt(lifetime.product_count)} sublabel="In master file" testid="tile-prod" />
            </div>

            {/* ---------- MILESTONE BOARD ---------- */}
            <MilestoneWidget lifetime={lifetime} />

            {/* ---------- RECORDS BOARD ---------- */}
            <Card className="border-2 border-amber-200 shadow-md">
              <CardHeader className="bg-gradient-to-r from-amber-100 via-yellow-50 to-amber-100 border-b border-amber-200 pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-amber-900">
                  <Trophy className="h-5 w-5" /> Records Board
                </CardTitle>
                <CardDescription>The biggest hits in your store's history</CardDescription>
              </CardHeader>
              <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {records.best_year && (
                  <RecordCard
                    icon={Crown}
                    color="amber"
                    title="Best Year"
                    value={records.best_year.year}
                    sub={`${fmtR(records.best_year.turnover)} turnover · ${fmtR(records.best_year.net_profit)} net`}
                  />
                )}
                {records.best_month_ever && (
                  <RecordCard
                    icon={Sparkles}
                    color="emerald"
                    title="Most Profitable Month"
                    value={`${records.best_month_ever.month_name} ${records.best_month_ever.year}`}
                    sub={`${fmtR(records.best_month_ever.gross_profit)} GP @ ${records.best_month_ever.gp_percent?.toFixed(1)}%`}
                  />
                )}
                {records.biggest_sale && (
                  <RecordCard
                    icon={Flame}
                    color="rose"
                    title="Biggest Single Sale"
                    value={fmtR(records.biggest_sale.amount)}
                    sub={`${records.biggest_sale.customer} · ${fmtDate(records.biggest_sale.date)}`}
                  />
                )}
                {records.busiest_day && (
                  <RecordCard
                    icon={Zap}
                    color="blue"
                    title="Busiest Day Ever"
                    value={fmtDate(records.busiest_day.date)}
                    sub={`${fmtR(records.busiest_day.turnover)} · ${fmtInt(records.busiest_day.transaction_count)} txns`}
                  />
                )}
              </CardContent>
            </Card>

            {/* ---------- DAY-OF-WEEK HEATMAP + PEAK HOUR ---------- */}
            {data?.day_of_week && (
              <DayOfWeekHeatmap dow={data.day_of_week} peakHour={data.peak_hour} />
            )}

            {/* ---------- STORE TIMELINE ---------- */}
            {data?.timeline?.length > 0 && (
              <StoreTimeline events={data.timeline} />
            )}

            {/* ---------- TOP-10 LEADERBOARDS ---------- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <TopList
                title="Top Customers"
                icon={Users}
                color="purple"
                rows={data?.top_customers || []}
                columns={[
                  { key: "name", label: "Customer", flex: 1 },
                  { key: "spend", label: "Spend", fmt: fmtR, align: "right", accent: true },
                  { key: "transactions", label: "Txns", fmt: fmtInt, align: "right" },
                ]}
                emptyText="No customer sales yet"
              />
              <TopList
                title="Top Products"
                icon={Package}
                color="blue"
                rows={data?.top_products || []}
                columns={[
                  { key: "name", label: "Product", flex: 1 },
                  { key: "units_sold", label: "Units", fmt: fmtInt, align: "right", accent: true },
                  { key: "revenue", label: "Revenue", fmt: fmtR, align: "right" },
                ]}
                emptyText="No products sold yet"
              />
              <TopList
                title="Top Cashiers"
                icon={Award}
                color="amber"
                rows={data?.top_cashiers || []}
                columns={[
                  { key: "name", label: "Cashier", flex: 1 },
                  { key: "transactions", label: "Txns", fmt: fmtInt, align: "right", accent: true },
                  { key: "turnover", label: "Turnover", fmt: fmtR, align: "right" },
                ]}
                emptyText="No cashier data"
                footer={
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full border-amber-300 text-amber-800 hover:bg-amber-50"
                    onClick={() => navigate("/reports/cashier-analytics")}
                    data-testid="compare-cashiers-btn"
                  >
                    <Trophy className="h-3.5 w-3.5 mr-1.5" />
                    Compare Cashiers (drill-down)
                  </Button>
                }
              />
            </div>

            {/* ---------- AVERAGE / PER-DAY / PER-YEAR ---------- */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Percent className="h-4 w-4 text-purple-600" /> The Big Picture
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs text-gray-600 uppercase font-semibold">Avg / Year</div>
                  <div className="text-xl font-bold text-purple-700">{fmtR(lifetime.average_per_year)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 uppercase font-semibold">Avg / Day</div>
                  <div className="text-xl font-bold text-purple-700">{fmtR(lifetime.average_per_day)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 uppercase font-semibold">Avg / Txn</div>
                  <div className="text-xl font-bold text-purple-700">{fmtR(lifetime.average_transaction_value)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 uppercase font-semibold">Net Margin</div>
                  <div className="text-xl font-bold text-purple-700">
                    {lifetime.net_margin?.toFixed(1)}%
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ---------- YEAR-OVER-YEAR CHART ---------- */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                  Year-over-Year — Turnover vs Net Profit
                </CardTitle>
                <CardDescription>Every year since day one</CardDescription>
              </CardHeader>
              <CardContent className="pt-2" style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtRShort} />
                    <Tooltip
                      formatter={(v, k) => k === "gp_percent" ? `${Number(v).toFixed(1)}%` : fmtR(v)}
                      labelStyle={{ fontWeight: 700 }}
                    />
                    <Legend />
                    <Bar dataKey="turnover" name="Turnover" fill="#7c3aed" radius={[6, 6, 0, 0]} />
                    <Line type="monotone" dataKey="net_profit" name="Net Profit" stroke="#059669" strokeWidth={3} dot={{ r: 5 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* ---------- YEARLY TABLE ---------- */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-600" /> Yearly Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b-2 border-gray-200 text-xs uppercase text-gray-600">
                      <tr>
                        <th className="text-left py-2 pr-3">Year</th>
                        <th className="text-right py-2 pr-3">Turnover</th>
                        <th className="text-right py-2 pr-3">Gross Profit</th>
                        <th className="text-right py-2 pr-3">GP %</th>
                        <th className="text-right py-2 pr-3">Expenses</th>
                        <th className="text-right py-2 pr-3">Net Profit</th>
                        <th className="text-right py-2 pr-3">Txns</th>
                        <th className="text-right py-2">Items</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearlyData.map((y) => (
                        <tr
                          key={y.year}
                          className={`border-b hover:bg-purple-50/50 ${records.best_year?.year === y.year ? "bg-amber-50" : ""}`}
                        >
                          <td className="py-2 pr-3 font-bold flex items-center gap-1">
                            {y.year}
                            {records.best_year?.year === y.year && (
                              <Crown className="h-3.5 w-3.5 text-amber-500" />
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right font-semibold">{fmtR(y.turnover)}</td>
                          <td className="py-2 pr-3 text-right text-emerald-700">{fmtR(y.gross_profit)}</td>
                          <td className="py-2 pr-3 text-right">
                            <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                              {y.gp_percent?.toFixed(1)}%
                            </Badge>
                          </td>
                          <td className="py-2 pr-3 text-right text-rose-700">{fmtR(y.expenses)}</td>
                          <td className={`py-2 pr-3 text-right font-bold ${y.net_profit >= 0 ? "text-emerald-800" : "text-rose-800"}`}>
                            {fmtR(y.net_profit)}
                          </td>
                          <td className="py-2 pr-3 text-right text-gray-700">{fmtInt(y.transaction_count)}</td>
                          <td className="py-2 text-right text-gray-700">{fmtInt(y.items_sold)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
